import { ReactNode, useContext } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Lms } from "@verity/api-client/types";
import { Id, ToastOptions, UpdateOptions, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { sleep } from "../helpers/util";
import { CoreToastContainer, coreToast, CoreToastContainerProps } from "@verity/components/core";
import { canAccessResource, UserPrivileges } from "../helpers/privilege";
import apiClient from "../helpers/api-client";
import { PortalContext } from "./portal-config";
import { PortalControllerContext } from "./context";

export const PORTAL_TOAST_CONTAINER_ID = "portal";

// #region Toast Context ---------------------------------------------------------------------------------------------

function genToastContainerId(compName: string): string {
	return `toast-YuJa-Verity-portal-${compName}-${new Date().getTime()}`;
}

export function genToastMessageId(compName: string, actionName: string): string {
	return `toast-YuJa-Verity-portal-${compName}-${actionName}-${new Date().getTime()}`;
}

function genToastContent(containerId: string, options?: CoreToastContainerProps): JSX.Element {
	return (
		<CoreToastContainer
			{...options}
			enableMultiContainer={true}
			containerId={containerId}
			position="top-center"
			newestOnTop={true}
		/>
	);
}

const genericToastLoadingOptions: ToastOptions & { title: string } = {
	autoClose: false,
	closeButton: false,
	closeOnClick: false,
	draggable: false,
	title: "",
};

function getToastLoadingOptions(containerId: string): ToastOptions {
	return { ...genericToastLoadingOptions, containerId: containerId };
}

const genericToastInfoOptions: ToastOptions = {
	autoClose: 5000,
	closeButton: true,
	closeOnClick: true,
	draggable: true,
};

function getToastInfoOptions(containerId: string): ToastOptions {
	return { ...genericToastInfoOptions, containerId: containerId };
}

const genericToastSuccessOptions: ToastOptions = {
	autoClose: 5000,
	closeButton: true,
	closeOnClick: true,
	draggable: true,
};

function getToastSuccessOptions(containerId: string): ToastOptions {
	return { ...genericToastSuccessOptions, containerId: containerId };
}

const genericToastErrorOptions: ToastOptions = {
	autoClose: false,
	closeButton: true,
	closeOnClick: true,
	draggable: true,
};

function getToastErrorOptions(containerId: string): ToastOptions {
	return { ...genericToastErrorOptions, containerId: containerId };
}

export type ToastInstance = {
	containerId: Id;
	jsxElement: JSX.Element;
	infoOptions: ToastOptions;
	loadingOptions: ToastOptions;
	successOptions: ToastOptions;
	errorOptions: ToastOptions;
};

export function genToastInstance(compName: string, options?: CoreToastContainerProps): ToastInstance {
	const containerId = genToastContainerId(compName);
	return {
		containerId: containerId,
		jsxElement: genToastContent(containerId, options),
		infoOptions: getToastInfoOptions(containerId),
		loadingOptions: getToastLoadingOptions(containerId),
		successOptions: getToastSuccessOptions(containerId),
		errorOptions: getToastErrorOptions(containerId),
	};
}

// #endregion Toast Context ------------------------------------------------------------------------------------------

// #region Toast Wrappers --------------------------------------------------------------------------------------------

/**
 * Dismiss all previous toast messages and log a new Toast info.
 * @param toastInstance
 * @param message
 * @param options optional additional options for the toast message.
 * @returns `Id` of emitted toast message.
 */
export function toastDismissAndInfo(toastInstance: ToastInstance, message: string, options?: ToastOptions): Id {
	toast.dismiss();
	return coreToast.info(message, {
		...toastInstance.infoOptions,
		...(options ?? {}),
	});
}

/**
 * Dismiss all previous toast messages and log a new Toast success.
 * @param toastInstance
 * @param message
 * @param options optional additional options for the toast message.
 * @returns `Id` of emitted toast message.
 */
export function toastDismissAndSuccess(toastInstance: ToastInstance, message: ReactNode, options?: ToastOptions): Id {
	toast.dismiss();
	return coreToast.success(message, {
		...toastInstance.successOptions,
		...(options ?? {}),
	});
}

/**
 * Dismiss all previous toast messages and log a new Toast error.
 * @param toastInstance
 * @param message
 * @param options optional additional options for the toast message.
 * @returns `Id` of emitted toast message.
 */
export function toastDismissAndError(toastInstance: ToastInstance, message: ReactNode, options?: ToastOptions): Id {
	toast.dismiss();
	return coreToast.error(message, {
		...toastInstance.errorOptions,
		...(options ?? {}),
	});
}

/**
 * Dismiss all previous toast messages and log a new Toast load message.
 * @param toastInstance
 * @param message
 * @param options optional additional options for the toast message.
 * @returns `Id` of emitted toast message.
 */
export function toastDismissAndLoad(toastInstance: ToastInstance, message?: string, options?: ToastOptions): Id {
	toast.dismiss();
	return coreToast.loading(message, {
		...toastInstance.loadingOptions,
		...(options ?? {}),
	});
}

/**
 * Update a previous loading toast.
 * @param toastInstance
 * @param loadId
 * @param type type of toast message to transform load toast to.
 * @param message
 * @param options optional update options.
 * @returns whether the update succeeded or not.
 */
export async function toastUpdateLoad(
	toastInstance: ToastInstance,
	loadId: Id,
	type: "success" | "error",
	message: string,
	options?: UpdateOptions,
): Promise<boolean> {
	let remainingRetries = 4;
	while (remainingRetries > 0) {
		if (toast.isActive(loadId)) {
			const defaultOptions =
				type === "success" ? { ...toastInstance.successOptions } : { ...toastInstance.errorOptions };
			coreToast.update(loadId, message, type, {
				...defaultOptions,
				...(options ?? {}),
			});
			return true;
		}
		await sleep(500);
		remainingRetries = remainingRetries - 1;
	}
	toast.dismiss();
	return false;
}

// #endregion Toast Wrappers -----------------------------------------------------------------------------------------

// #region React Comps -----------------------------------------------------------------------------------------------

/**
 * Use to easily share React states between different components.
 */
export type ReactState<T> = {
	state: T;
	set: React.Dispatch<React.SetStateAction<T>>;
};

export type TooltipWrapperProps = {
	key?: string | number;
	fontPxSize?: number;
};
/**
 *
 * @param text tooltip text.
 * @param innerComp the JSX element on which to display the tooltip.
 * @param props optional props for the tooltip.
 * @returns
 */
export const TooltipWrapper = (text: string, innerComp: JSX.Element, props?: TooltipWrapperProps) => {
	const defaultFontPxSize = "13px";
	const titleSpan = (
		<span
			style={{
				fontSize: props?.fontPxSize ? `${props.fontPxSize.toString()}px` : defaultFontPxSize,
			}}
		>
			{text}
		</span>
	);
	return props?.key == null ? (
		<Tooltip title={titleSpan} arrow placement="top">
			{innerComp}
		</Tooltip>
	) : (
		<Tooltip key={props.key} title={titleSpan} arrow placement="top">
			{innerComp}
		</Tooltip>
	);
};

// #endregion React Comps --------------------------------------------------------------------------------------------

// #region Error Paths -----------------------------------------------------------------------------------------------

export const error400 = "/400.html";
export const error401 = "/401.html";
export const error403 = "/403.html";
export const error404 = "/404.html";
export const error500 = "/500.html";
export const error503 = "/503.html";

// #endregion Error Paths --------------------------------------------------------------------------------------------

// #region Fetch Helpers ---------------------------------------------------------------------------------------------

export type CheckFetchChangedProp = {
	fetchLink: string;
	lastETag?: string;
};
export type FetchChangedResponse = {
	fetchLink: string;
	changed: boolean;
	newETag?: string;
};

/**
 * Checks whether provided resource links have changed since the last fetch.
 * @param props each prop contains the `fetchLink` to check and its `lastETag` if known.
 * @returns each provided `fetchLink` with Boolean `changed` and `newETag` to be saved.
 */
export async function checkFetchChanged(props: CheckFetchChangedProp[]): Promise<FetchChangedResponse[]> {
	try {
		// Fetch
		const resp = await Promise.all(
			props.map((x) => {
				return apiClient.fetch(x.fetchLink);
			}),
		);
		const newProps = props.map((x, i) => {
			return { ...x, newETag: resp[i].headers.get("Etag") };
		});
		// Loop tags
		const ret: FetchChangedResponse[] = [];
		for (const prop of newProps) {
			const changed = prop.lastETag != null && prop.newETag != null && prop.lastETag !== prop.newETag;
			ret.push({
				fetchLink: prop.fetchLink,
				changed: changed,
				newETag: prop.newETag ?? undefined,
			});
		}
		return ret;
	} catch (error) {
		return props.map((x) => {
			return { ...x, changed: false };
		});
	}
}

// #endregion Fetch Helpers ------------------------------------------------------------------------------------------

export function isInstructor(
	privileges: UserPrivileges,
	portalContext: Pick<PortalContext, "instituteUuid" | "courseUuid"> = {},
) {
	return canAccessResource(
		{ privileges },
		{
			privilege: "ASSESSMENTS",
			action: "UPDATE",
			resourceScope: { instituteId: portalContext.instituteUuid, courseId: portalContext.courseUuid },
		},
	);
}

export function isAdmin(privileges: UserPrivileges, portalContext: Pick<PortalContext, "instituteUuid"> = {}) {
	return canAccessResource(
		{ privileges },
		{
			privilege: "INSTITUTE",
			action: "UPDATE",
			resourceScope: { instituteId: portalContext.instituteUuid, resourceId: portalContext.instituteUuid },
		},
	);
}

export function isAccountManager(privileges: UserPrivileges, portalContext: Pick<PortalContext, "instituteUuid"> = {}) {
	return canAccessResource(
		{ privileges },
		{
			privilege: "INSTITUTE/FEATURE",
			action: "UPDATE",
			resourceScope: { instituteId: portalContext.instituteUuid, resourceId: portalContext.instituteUuid },
		},
	);
}

export function useIsInstructor() {
	const { portalContext, userPrivileges } = useContext(PortalControllerContext);
	return isInstructor(userPrivileges, portalContext);
}

export function useIsAdmin() {
	const { portalContext, userPrivileges } = useContext(PortalControllerContext);
	return isAdmin(userPrivileges, portalContext);
}

export function useIsAccountManager() {
	const { portalContext, userPrivileges } = useContext(PortalControllerContext);
	return isAccountManager(userPrivileges, portalContext);
}

export function replaceWithPortalHostname(
	originalUrl: string,
	instituteIntegrationHostname?: string,
	portalHostname?: string,
) {
	const url = new URL(originalUrl);
	if (!portalHostname || url.hostname !== instituteIntegrationHostname || url.hostname === portalHostname) {
		return originalUrl;
	}
	url.hostname = portalHostname;
	return url.toString();
}

export function convertPortalContextToLmsName({
	lms,
	lmsDetails,
}: PortalContext): Lms | "BlackboardLearnUltra" | undefined {
	if (lms === "BlackboardLearn" && lmsDetails?.type === "ultra") {
		return "BlackboardLearnUltra";
	}
	return lms;
}

export function getLogoutUrl(homeOrigin: string) {
	return `${homeOrigin}/login/home`;
}
